import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { toast } from 'sonner';

import { getMe } from '@/lib/me';
import { useRouter } from '@/lib/nav';

import type { Me } from '@alianca/core/contracts/me';

export type Persona = Me['persona'];

const MeContext = createContext<Me | null>(null);

/** Carrega o usuário uma vez para a área logada; os filhos só renderizam com `me` pronto (como o `beforeLoad` do Vite). */
export function MeProvider({ children }: { children: ReactNode }) {
    const [me, setMe] = useState<Me | null>(null);
    const [falhou, setFalhou] = useState(false);

    useEffect(() => {
        let ativo = true;
        getMe()
            .then((m) => ativo && m && setMe(m))
            .catch(() => ativo && setFalhou(true));
        return () => {
            ativo = false;
        };
    }, []);

    if (falhou) {
        return <div className="p-8 text-body text-crit">Não foi possível carregar seu perfil. Atualize a página.</div>;
    }
    if (!me) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-green" />
            </div>
        );
    }
    return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

export function useMe(): Me {
    const me = useContext(MeContext);
    if (!me) throw new Error('useMe() fora do MeProvider');
    return me;
}

/** Guard de UX da página (o backend decide o acesso): persona fora da lista vai para `/dashboard`. */
export function usePersonaGuard(personas: readonly Persona[], mensagem?: string): boolean {
    const { persona } = useMe();
    const router = useRouter();
    const permitido = personas.includes(persona);

    useEffect(() => {
        if (permitido) return;
        router.push('/dashboard');
        if (mensagem) toast.error(mensagem);
    }, [permitido, router, mensagem]);

    return permitido;
}
